import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChatWindow } from "./types";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import {
  IconArrowLeft, PrimaryBlueButton, Severity
} from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import TabBar from "./Tabs/TabBar";
import Appearance from "./Tabs/Appearance/Appearance";
import Widgets from "./Tabs/Widgets/Widgets";
import Channels from "./Tabs/Channels/Channels";
import PreChatSurvey from "./Tabs/PreChatSurvey";
import PostChatSurvey from "./Tabs/PostChatSurvey";
import Settings from "./Tabs/Settings/Settings";
import { LanguageConfigModel, StyleConfigModel } from "../types";
import Edit from "@/kb/articles/Edit";
import EditableLabel from "./EditableLabel";
import { useHistoryState } from "./useHistoryState";

const defaultChatWindow: ChatWindow = {
  id: 0,
  siteId: 0,
  name: "",
  updateTimestamp: "",
  createdByUserName: "",
  styleConfig: {} as StyleConfigModel,
  languageConfig: {} as LanguageConfigModel
};

const tabs = [
  "Appearance",
  "Widgets",
  "Channels"
  // "Pre-chat Survey",
  // "Post-chat Survey",
  // "Settings"
];

export default function ChatWindowEditor() {
  const { chatWindowId: stringChatWindowId } = useParams<{
    chatWindowId: string;
  }>();
  const [chatWindow, setChatWindow] = useState<ChatWindow>(defaultChatWindow);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const {
    state: workingChatWindow,
    set: setWorkingChatWindow,
    replace: replaceWorkingChatWindow,
    undo,
    redo,
    canUndo,
    canRedo
  } = useHistoryState<ChatWindow>(defaultChatWindow);
  const [saving, setSaving] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const dialogOpenerRef = useRef<HTMLElement | null>(null);
  const [stagedLogoFile, setStagedLogoFile] = React.useState<File | null>(null);
  const [stagedGraphicFile, setStagedGraphicFile] = React.useState<File | null>(null);
  const [stagedHeaderBgFile, setStagedHeaderBgFile] = React.useState<File | null>(null);
  const [stagedLauncherIconFile, setStagedLauncherIconFile] = React.useState<File | null>(null);
  const [stagedContentWidgetImageFile, setStagedContentWidgetImageFile] = React.useState<File | null>(null);

  const api = useApi();
  const toastAlert = useToastAlert();
  const navigate = useNavigate();

  useEffect(() => {
    if (!showUnsavedDialog) return;
    dialogOpenerRef.current = document.activeElement as HTMLElement | null;
    // Focus first focusable control inside dialog
    const raf = requestAnimationFrame(() => {
      const node = dialogRef.current;
      if (!node) return;
      const first = node.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      first?.focus();
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowUnsavedDialog(false);
        return;
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKey);
      // Return focus to opener
      dialogOpenerRef.current?.focus?.();
    };
  }, [showUnsavedDialog]);

  const isDirty =
    JSON.stringify(workingChatWindow) !== JSON.stringify(chatWindow) ||
    stagedLogoFile !== null ||
    stagedGraphicFile !== null ||
    stagedHeaderBgFile !== null ||
    stagedLauncherIconFile !== null ||
    stagedContentWidgetImageFile !== null;

  useEffect(() => {
    if (!stringChatWindowId) {
      console.error("Chat window ID is required");
      return;
    }

    async function fetchChatWindow() {
      setLoading(true);
      const chatWindowId = Number(stringChatWindowId);
      if (isNaN(chatWindowId)) {
        toastAlert.displayToast(Severity.Error, "Invalid chat window ID.");
        setLoading(false);
        return;
      }
      const response = await api.messaging.get(
        `ChatWindow/Get/${chatWindowId}`
      );
      if (!response.ok) {
        const errorText = await response.text();
        toastAlert.displayToast(Severity.Error, errorText);
        setLoading(false);
        return;
      }
      const chatWindow = (await response.json()) as ChatWindow;
      chatWindow.styleConfig = chatWindow.styleConfig || ({} as StyleConfigModel);
      chatWindow.languageConfig = chatWindow.languageConfig || ({} as LanguageConfigModel);
      setChatWindow(chatWindow);
      replaceWorkingChatWindow(chatWindow);
      setLoading(false);
    }
    fetchChatWindow();
  }, [api.messaging, stringChatWindowId, toastAlert, replaceWorkingChatWindow]);

  // Keyboard shortcuts: Ctrl/Cmd+Z = undo, Ctrl/Cmd+Shift+Z = redo.
  // Also Escape to close the unsaved-changes dialog.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const mod = e.ctrlKey || e.metaKey;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const isEditable =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (target && (target as HTMLElement).isContentEditable);

      if (e.key === "Escape" && showUnsavedDialog) {
        setShowUnsavedDialog(false);
        return;
      }
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        if (isEditable) return;
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        if (isEditable) return;
        e.preventDefault();
        redo();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [undo, redo, showUnsavedDialog]);

  async function saveChanges() {
    setSaving(true);
    try {
      const formData = new FormData();

      // Build styleConfig for the request without mutating React state.
      // When a file is staged, clear the URL from the payload so the server
      // uses the uploaded file instead of the old base64/URL value.
      const styleConfigPayload = { ...workingChatWindow.styleConfig };
      if (stagedLogoFile) {
        formData.append("logo", stagedLogoFile);
        styleConfigPayload.logoUrl = undefined;
      }
      if (stagedGraphicFile) {
        formData.append("queueGraphic", stagedGraphicFile);
        styleConfigPayload.queueGraphicUrl = undefined;
      }
      if (stagedHeaderBgFile) {
        formData.append("headerBgImage", stagedHeaderBgFile);
        styleConfigPayload.headerBackgroundImageUrl = undefined;
      }
      if (stagedLauncherIconFile) {
        formData.append("launcherIcon", stagedLauncherIconFile);
        styleConfigPayload.launcherIconUrl = undefined;
      }
      if (stagedContentWidgetImageFile) {
        formData.append("contentWidgetImage", stagedContentWidgetImageFile);
        styleConfigPayload.contentWidgetImageUrl = undefined;
      }
      formData.append("name", workingChatWindow.name);
      formData.append("styleConfig", JSON.stringify(styleConfigPayload));
      formData.append("languageConfig", JSON.stringify(workingChatWindow.languageConfig));

      const response = await api.messaging.postFormData(
        `ChatWindow/Update/${workingChatWindow.id}`,
        formData
      );

      if (!response.ok) {
        const errorText = await response.text();
        toastAlert.displayToast(Severity.Error, errorText);
        return;
      }

      toastAlert.displayToast(Severity.Success, "Changes saved");

      const responseChatWindow = (await response.json()) as ChatWindow;
      responseChatWindow.styleConfig = responseChatWindow.styleConfig || ({} as StyleConfigModel);
      responseChatWindow.languageConfig = responseChatWindow.languageConfig || ({} as LanguageConfigModel);
      setChatWindow(responseChatWindow);
      replaceWorkingChatWindow(responseChatWindow);
      setStagedLogoFile(null);
      setStagedGraphicFile(null);
      setStagedHeaderBgFile(null);
      setStagedLauncherIconFile(null);
      setStagedContentWidgetImageFile(null);
    } catch (ex: any) {
      toastAlert.displayToast(Severity.Error, "Failed to save changes.");
      console.error("saveChanges failed:", ex?.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div
        className="sticky top-0 z-10 bg-slate-50 -mx-6 px-6 pt-4 pb-2 relative"
      >
        <div className="absolute left-0 right-0 -top-3 sm:-top-6 h-3 sm:h-6 bg-slate-50" aria-hidden="true" />
        <div className="flex justify-between">
          <div className="flex items-center">
            <button
              type="button"
              aria-label="Back"
              onClick={() => {
                if (isDirty) {
                  setShowUnsavedDialog(true);
                } else {
                  navigate(-1);
                }
              }}
              className="inline-flex items-center justify-center min-w-[24px] min-h-[24px] p-1 rounded-md text-slate-500 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
            >
              <IconArrowLeft />
            </button>
            <EditableLabel
              value={workingChatWindow.name}
              onChange={(newValue) =>
                setWorkingChatWindow({ ...workingChatWindow, name: newValue })
              }
            />
          </div>
          <div className="flex items-center gap-3">
            {isDirty && (
              <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
            )}
            <span role="status" aria-live="polite" className="sr-only">
              {saving ? "Saving" : isDirty ? "Unsaved changes" : ""}
            </span>
            <button
              type="button"
              onClick={undo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
              aria-label="Undo"
              className="inline-flex items-center justify-center w-8 h-8 min-w-[32px] min-h-[32px] rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M7.28 3.22a.75.75 0 010 1.06L5.56 6h7.69a4.75 4.75 0 010 9.5h-2.5a.75.75 0 010-1.5h2.5a3.25 3.25 0 000-6.5H5.56l1.72 1.72a.75.75 0 11-1.06 1.06l-3-3a.75.75 0 010-1.06l3-3a.75.75 0 011.06 0z" clipRule="evenodd" />
              </svg>
            </button>
            <button
              type="button"
              onClick={redo}
              disabled={!canRedo}
              title="Redo (Ctrl+Shift+Z)"
              aria-label="Redo"
              className="inline-flex items-center justify-center w-8 h-8 min-w-[32px] min-h-[32px] rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M12.72 3.22a.75.75 0 011.06 0l3 3a.75.75 0 010 1.06l-3 3a.75.75 0 11-1.06-1.06L14.44 7.5H6.75a3.25 3.25 0 000 6.5h2.5a.75.75 0 010 1.5h-2.5a4.75 4.75 0 010-9.5h7.69l-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
              </svg>
            </button>
            <PrimaryBlueButton
              label={saving ? "Saving…" : "Save"}
              onClick={saveChanges}
            />
          </div>
        </div>
        <TabBar
          tabs={tabs}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      </div>
      <div className="p-4 mt-6">
        {selectedTab === "Appearance" && (
          <Appearance
            workingChatWindow={workingChatWindow}
            setWorkingChatWindow={setWorkingChatWindow}
            setStagedLogoFile={setStagedLogoFile}
            setStagedGraphicFile={setStagedGraphicFile}
            setStagedHeaderBgFile={setStagedHeaderBgFile}
          />
        )}
        {selectedTab === "Widgets" && (
          <Widgets
            workingChatWindow={workingChatWindow}
            setWorkingChatWindow={setWorkingChatWindow}
            setStagedContentWidgetImageFile={setStagedContentWidgetImageFile}
          />
        )}
        {selectedTab === "Channels" && (
          <Channels
            workingChatWindow={workingChatWindow}
            setWorkingChatWindow={setWorkingChatWindow}
          />
        )}
        {/* {selectedTab === "Pre-chat Survey" && <PreChatSurvey />}
      {selectedTab === "Post-chat Survey" && <PostChatSurvey />}
      {selectedTab === "Settings" && <Settings />} */}
      </div>

      {showUnsavedDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowUnsavedDialog(false)}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="unsaved-dialog-title"
            aria-describedby="unsaved-dialog-desc"
            className="bg-white rounded-xl shadow-xl p-6 w-96 max-w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div id="unsaved-dialog-title" className="text-base font-semibold mb-2">Unsaved changes</div>
            <p id="unsaved-dialog-desc" className="text-sm text-slate-600 mb-5">
              You have unsaved changes. Do you want to save before leaving?
            </p>
            <div className="flex gap-2 justify-end">
              <button
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 min-h-[36px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
                onClick={() => setShowUnsavedDialog(false)}
              >
                Keep editing
              </button>
              <button
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 text-red-700 min-h-[36px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
                onClick={() => { setShowUnsavedDialog(false); navigate(-1); }}
              >
                Discard & leave
              </button>
              <button
                className="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:opacity-90 min-h-[36px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
                onClick={async () => { await saveChanges(); setShowUnsavedDialog(false); navigate(-1); }}
              >
                Save & leave
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
